/**
 * Pointer-driven 3D tilt for Shop (and similar) cards — Apple Music–style parallax.
 * Desktop/pointer only; skipped for coarse touch so cards stay stable on phones.
 */

const MAX_TILT_DEG = 11;
const LIFT_PX = 2;
const FINE_POINTER = "(hover: hover) and (pointer: fine)";

function prefersFinePointer(): boolean {
  return typeof window !== "undefined" && window.matchMedia(FINE_POINTER).matches;
}

function setTilt(el: HTMLElement, rx: number, ry: number, lift: number) {
  el.style.setProperty("--tilt-rx", `${rx.toFixed(2)}deg`);
  el.style.setProperty("--tilt-ry", `${ry.toFixed(2)}deg`);
  el.style.setProperty("--tilt-lift", `${lift.toFixed(2)}px`);
}

function resetTilt(el: HTMLElement) {
  setTilt(el, 0, 0, 0);
  el.classList.remove("shopCardTilt--active");
}

/**
 * Bind tilt to a card. Safe to call multiple times (no-ops if already bound).
 * Uses CSS variables consumed by `.shopCardTilt` styles.
 */
export function bindCardTilt(card: HTMLElement): void {
  if (card.dataset.tiltBound === "1") return;
  card.dataset.tiltBound = "1";
  card.classList.add("shopCardTilt");

  const onMove = (e: PointerEvent) => {
    if (!prefersFinePointer()) return;
    if (e.pointerType === "touch") return;
    const rect = card.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return;
    const nx = (e.clientX - rect.left) / rect.width; // 0..1
    const ny = (e.clientY - rect.top) / rect.height;
    const ry = (nx - 0.5) * 2 * MAX_TILT_DEG;
    const rx = (0.5 - ny) * 2 * MAX_TILT_DEG;
    card.classList.add("shopCardTilt--active");
    setTilt(card, rx, ry, LIFT_PX);
  };

  const onLeave = () => {
    resetTilt(card);
  };

  card.addEventListener("pointerenter", onMove);
  card.addEventListener("pointermove", onMove);
  card.addEventListener("pointerleave", onLeave);
  card.addEventListener("pointercancel", onLeave);
}
